import LegalShell, { H2, H3, P, UL } from './LegalShell';
import { COMPANY, formatAddress } from './companyInfo';

// Pro Haul Privacy Policy.
//
// THIS IS INITIAL TEMPLATE COPY suitable for Stripe activation
// verification and early customer transparency. Have it reviewed by
// counsel before relying on for regulatory compliance (CCPA, GLBA,
// FCRA for driver background checks, etc.). Edit companyInfo.js (not
// this file) to swap entity name, address, support / privacy emails,
// and effective date.

export default function Privacy() {
  return (
    <LegalShell title="Privacy Policy">
      <P>
        This Privacy Policy describes how {COMPANY.legalName} (&ldquo;{COMPANY.productName},&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) collects, uses, and shares
        information when you use the {COMPANY.productName} platform (the
        &ldquo;<strong>Service</strong>&rdquo;). By using the Service you consent to the
        practices described here.
      </P>

      <H2>1. Information We Collect</H2>

      <H3>Account &amp; Carrier Information</H3>
      <P>
        When you create an account or are invited to one, we collect your email address,
        password (stored hashed), your name (if provided), the carrier you belong to,
        and your role within that carrier (owner, admin, HR, accounting, dispatch, or
        driver).
      </P>

      <H3>Carrier-Submitted Data</H3>
      <P>
        Carriers using the Service may submit data about their operations and personnel,
        including but not limited to: driver names, license numbers, medical card
        information, employment history, road test certificates, vehicle and equipment
        records, load and trip records, broker and customer contact information,
        documents (rate confirmations, BOLs, maintenance records, invoices), and
        accounting entries.
      </P>
      <P>
        Carriers are the controllers of this data and are responsible for the legality of
        their submission, including obtaining required consents from drivers and other
        individuals. {COMPANY.productName} processes this data on the carrier&rsquo;s
        behalf solely to provide the Service.
      </P>

      <H3>Payment Information</H3>
      <P>
        Subscription payments are processed by Stripe, Inc. We do not collect, store, or
        have access to your full card number. We receive only a tokenised reference to
        your payment method, the last four digits of your card, and billing metadata
        (subscription status, invoice history, billing address you provide to Stripe).
      </P>

      <H3>Usage &amp; Technical Information</H3>
      <P>
        We automatically collect basic technical information when you use the Service,
        including IP address, browser type, device identifiers, pages visited, and
        timestamps. This information is used for security monitoring, debugging, and
        product improvement.
      </P>

      <H3>Location &amp; GPS Information</H3>
      <P>
        When you use load tracking, active-load routing, ETA reporting, or driver-status
        features that depend on geographic position, we may collect precise location
        data (latitude/longitude coordinates and movement events) from your device.
        Location data is used to power load assignment matching, route optimisation,
        delivery ETAs, IFTA mileage reporting, and driver-status visibility for your
        dispatcher. You can disable location sharing at any time from your device&rsquo;s
        operating-system settings (iOS: <em>Settings &rarr; Privacy &amp; Security
        &rarr; Location Services</em>; Android: <em>Settings &rarr; Location</em>).
        Disabling location may reduce or disable certain Service features.
      </P>

      <H3>Communications</H3>
      <P>
        When you contact us by email or in-app, we retain the message and our response
        for support history and quality purposes.
      </P>

      <H2>2. How We Use Information</H2>
      <UL>
        <li>Provide, operate, and maintain the Service;</li>
        <li>Process subscriptions, payments, and refunds via Stripe;</li>
        <li>
          Deliver compliance services you order through the Service (for example, drug
          testing, motor vehicle records, FMCSA Clearinghouse queries), which requires
          sharing necessary data with the relevant third-party provider;
        </li>
        <li>Send transactional emails (invite confirmations, billing receipts, account notices);</li>
        <li>
          Send transactional and operational SMS messages (load assignment alerts,
          driver-document expiry warnings, security codes, billing notifications) to the
          mobile number you provide;
        </li>
        <li>
          Send promotional and marketing communications about new features, product
          announcements, pricing changes, and Pro Haul offerings &mdash; you may opt out
          of these at any time (see &ldquo;Your Rights &amp; Choices&rdquo; below);
        </li>
        <li>Respond to your support requests;</li>
        <li>
          Power optional artificial-intelligence features (for example, parsing
          rate-confirmation PDFs or maintenance invoices) when you use them &mdash; in
          which case the relevant document content is sent to our AI provider for
          processing on demand;
        </li>
        <li>
          Generate routing and mileage data through our mapping provider when you use
          load planning or IFTA features;
        </li>
        <li>Detect, investigate, and prevent fraud, abuse, and security incidents;</li>
        <li>
          Comply with legal obligations, including DOT/FMCSA record retention rules
          carriers are subject to under 49 CFR;
        </li>
        <li>Analyse aggregated usage to improve product quality.</li>
      </UL>

      <H2>3. How We Share Information</H2>
      <P>
        We do <strong>not</strong> sell your information. We share information only as
        described below:
      </P>

      <H3>Mobile Information &amp; SMS</H3>
      <UL>
        <li>
          <strong>
            Notwithstanding any other provision in this policy, mobile information
            collected for text (SMS) communications will not be shared with, sold, or
            conveyed to third parties or affiliates for their marketing or promotional
            purposes.
          </strong>
        </li>
      </UL>

      <H3>Service Providers</H3>
      <P>
        We share information with vendors that help us operate the Service. Each is
        contractually bound to handle data only as needed to provide their service.
        Principal providers include:
      </P>
      <UL>
        <li><strong>Supabase</strong> &mdash; database hosting, authentication, file storage</li>
        <li><strong>Stripe</strong> &mdash; subscription billing, payment processing, invoicing</li>
        <li><strong>Resend</strong> &mdash; transactional email delivery</li>
        <li><strong>Anthropic</strong> &mdash; AI document parsing and dispatch chat (only data you submit to these features)</li>
        <li><strong>Trimble / PC*Miler</strong> &mdash; routing, mileage, and address verification</li>
        <li><strong>FMCSA</strong> &mdash; carrier safety data, PSP reports, Clearinghouse queries (when those features are used)</li>
        <li><strong>Cloudflare</strong> &mdash; DNS and edge network</li>
        <li><strong>Railway</strong> &mdash; application hosting</li>
        <li>Compliance-services third-party administrators, laboratories, and data furnishers (when those services are ordered)</li>
      </UL>

      <H3>Within Your Carrier Workspace</H3>
      <P>
        Data you submit to your carrier workspace is visible to other authorised
        members of the same carrier in accordance with their roles. Data is
        tenant-isolated: members of one carrier cannot see data belonging to another
        carrier.
      </P>

      <H3>Legal &amp; Safety</H3>
      <P>
        We may disclose information if required by law, subpoena, or other legal
        process; to protect the rights, property, or safety of {COMPANY.productName},
        our users, or others; or in connection with a corporate transaction (merger,
        acquisition, asset sale), in which case we will notify affected carriers.
      </P>

      <H2>4. Data Security</H2>
      <P>
        We use industry-standard security measures, including encryption in transit
        (HTTPS/TLS) and at rest, role-based access controls, multi-tenant isolation
        enforced at the database level, and regular security review of our hosting and
        vendor stack. No system is perfectly secure; we cannot guarantee absolute
        security, and you are responsible for safeguarding your account credentials.
      </P>

      <H2>5. Data Retention</H2>
      <P>
        We retain account and Customer Data for as long as your account is active. After
        account closure, we may retain certain data for a reasonable period to permit
        export, fulfil legal record-retention obligations (including driver qualification
        file retention under 49 CFR 391.51), resolve disputes, and enforce our
        agreements.
      </P>
      <P>
        Carriers can request deletion of specific Customer Data at any time by contacting
        us at{' '}
        <a href={`mailto:${COMPANY.privacyEmail}`} className="text-prohaul-red hover:underline">
          {COMPANY.privacyEmail}
        </a>{' '}
        &mdash; subject to the retention obligations above.
      </P>

      <H2>6. Your Rights &amp; Choices</H2>
      <P>
        Depending on your jurisdiction, you may have the right to:
      </P>
      <UL>
        <li>Access the personal information we hold about you;</li>
        <li>Request correction of inaccurate information;</li>
        <li>Request deletion (subject to retention obligations);</li>
        <li>Request an export of your data in a portable format;</li>
        <li>Object to or restrict certain processing;</li>
        <li>Withdraw consent where processing is based on consent.</li>
      </UL>
      <P>
        To exercise any of these rights, contact us at{' '}
        <a href={`mailto:${COMPANY.privacyEmail}`} className="text-prohaul-red hover:underline">
          {COMPANY.privacyEmail}
        </a>
        . If you are a driver whose information was submitted by a carrier, please
        contact your employing carrier first, as they are the controller of that data.
      </P>

      <H3>Communication Choices</H3>
      <P>You can control how we communicate with you at any time:</P>
      <UL>
        <li>
          <strong>Promotional email</strong> &mdash; click the
          &ldquo;unsubscribe&rdquo; link at the bottom of any marketing email, or
          email{' '}
          <a href={`mailto:${COMPANY.privacyEmail}`} className="text-prohaul-red hover:underline">
            {COMPANY.privacyEmail}
          </a>{' '}
          to be removed from marketing lists. Transactional and account-related emails
          (invoices, security alerts, invitation confirmations) will continue while
          your account is active.
        </li>
        <li>
          <strong>SMS / text messages</strong> &mdash; reply <strong>STOP</strong> to
          any text from us to opt out of promotional SMS; reply <strong>HELP</strong>{' '}
          for help. Standard messaging and data rates may apply. Operational SMS
          required for the Service (security codes, billing failures, legally required
          notices) may continue after a promotional opt-out so long as your account
          remains active.
        </li>
        <li>
          <strong>Location &amp; GPS</strong> &mdash; turn location sharing on or off
          at any time in your device&rsquo;s operating-system settings (iOS:{' '}
          <em>Settings &rarr; Privacy &amp; Security &rarr; Location Services</em>;
          Android: <em>Settings &rarr; Location</em>). Disabling location may reduce
          or disable load tracking, ETAs, and related routing features.
        </li>
        <li>
          <strong>In-app preferences</strong> &mdash; visit your account settings to
          adjust notification preferences within the Service.
        </li>
      </UL>

      <H2>7. Cookies &amp; Local Storage</H2>
      <P>
        The Service uses local storage and cookies for essential functions only,
        including authentication session tokens and remembering your in-app preferences
        (e.g. last-seen timestamps for messages). We do not use third-party advertising
        or analytics cookies that track you across other sites.
      </P>

      <H2>8. Children</H2>
      <P>
        The Service is not directed to anyone under the age of 18, and we do not
        knowingly collect personal information from minors. If you become aware that a
        minor has submitted personal information, please contact us and we will delete
        the information.
      </P>

      <H2>9. International Users</H2>
      <P>
        The Service is operated from the United States and our service providers are
        primarily US-based. If you access the Service from outside the United States,
        you understand that your information will be transferred to and processed in
        the United States.
      </P>

      <H2>10. Changes to This Policy</H2>
      <P>
        We may update this Privacy Policy from time to time. If we make a material
        change, we will notify you by email or in-app notice and update the
        &ldquo;Effective&rdquo; date at the top of this page. Your continued use of
        the Service after the revised policy takes effect constitutes acceptance of
        the changes.
      </P>

      <H2>11. Contact</H2>
      <P>
        Privacy questions or requests can be sent to{' '}
        <a href={`mailto:${COMPANY.privacyEmail}`} className="text-prohaul-red hover:underline">
          {COMPANY.privacyEmail}
        </a>{' '}
        or by mail to:
      </P>
      <P>
        {COMPANY.legalName}<br />
        Attn: Privacy<br />
        {formatAddress()}
      </P>
    </LegalShell>
  );
}
